/**
 * User Attributes Component
 * Displays and manages user-specific attribute values
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  effect,
  inject,
  input,
  signal,
  computed,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AttributeDefinitionService } from '../../core/services/attribute-definition.service';
import { APP_SCHEMA_ENDPOINTS } from '../../core/api';
import { UserAttributeWithDefinition } from '../../core/models';
import { TranslatePipe } from '../../core/features/translations';
import { UserRoleService } from '../../services/user-role.service';

export interface FkLookupOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-user-attributes',
  imports: [CommonModule, FormsModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="bg-white rounded-lg font-sans">
      @if (initialLoading()) {
        <div class="text-xs text-gray-500 italic mb-3">{{ 'portal.mydata.attributes.loading' | translate }}</div>
      }

      <!-- Auto-save Info -->
      <div class="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded mb-4 text-xs">
        <span class="text-base">ℹ️</span>
        <span class="text-blue-700 font-medium">Änderungen werden automatisch gespeichert</span>
      </div>

      @if (error()) {
        <div class="p-3 bg-red-50 border border-red-200 rounded text-red-700 flex items-center gap-2 text-xs">
          <span class="text-base">⚠️</span>
          {{ error() }}
        </div>
      }

      @if (!initialLoading() && !error() && userAttributes().length > 0) {
        <div class="flex flex-col gap-2">
          @for (group of userAttributes(); track group.attribute_id) {
            @if (group.is_group && group.children.length > 0) {
              <div class="border border-gray-200 rounded-md bg-gray-50/50 overflow-hidden transition-all duration-200 hover:border-gray-300">
                <div class="flex items-center gap-2.5 cursor-pointer select-none p-3 px-4 transition-colors duration-200 hover:bg-black/5" 
                     (click)="toggleGroup(group.attribute_id)">
                  <span class="text-[0.75rem] text-gray-500 transition-transform duration-200 inline-block w-4 text-center"
                        [class.-rotate-90]="isGroupCollapsed(group.attribute_id)">
                    ▼
                  </span>
                  <h4 class="m-0 text-[0.95rem] font-semibold text-gray-800 flex-1">
                    {{ group.attribute_description || group.attribute_name | translate }}
                  </h4>
                </div>

                @if (!isGroupCollapsed(group.attribute_id)) {
                  <div class="flex flex-col gap-3 p-4 pt-0 border-t border-gray-200">
                    @if (hasBulletListItems(group.children)) {
                      <div class="flex flex-col gap-3 py-2">
                        @for (child of group.children; track getChildTrackId(child)) {
                          @if (child.attribute_data_type && child.attribute_data_type.toUpperCase() === 'BULLET_LIST') {
                            <div class="flex items-center gap-2">
                              <label class="flex items-center gap-3 cursor-pointer p-3 px-4 border-2 border-gray-200 rounded-lg bg-white transition-all duration-200 flex-1 hover:border-blue-600 hover:bg-blue-50/20"
                                     [class.border-blue-600.bg-blue-50/10]="child.attribute_value === 'true'">
                                <input
                                  type="radio"
                                  [name]="'bulletlist_' + group.attribute_id"
                                  [checked]="child.attribute_value === 'true'"
                                  (change)="onBulletListChange(group, child)"
                                  class="w-5 h-5 cursor-pointer accent-blue-600"
                                />
                                <span class="text-sm text-gray-800 font-medium flex-1">
                                  {{ child.attribute_description || child.attribute_name | translate }}{{ child.school_shortname ? ' - ' + child.school_shortname : '' }}
                                </span>
                              </label>
                              @if (savingAttributeId() === child.attribute_id) {
                                <span class="text-base animate-pulse">💾</span>
                              }
                            </div>
                          }
                        }
                      </div>
                    } @else {
                      @for (child of group.children; track getChildTrackId(child)) {
                        <div class="flex flex-col gap-1.5 mt-3">
                          <label class="text-sm font-medium text-gray-700">
                            {{ child.attribute_description || child.attribute_name | translate }}{{ child.school_shortname ? ' - ' + child.school_shortname : '' }}
                          </label>

                          <div class="flex items-center gap-2">
                            @if (isReadOnlyAttribute(group, child)) {
                              <!-- READ-ONLY ATTRIBUTES (e.g. Teacher Attributes from SwissSalary / SWS) -->
                              <div class="flex-1 p-2 px-3 bg-gray-100/80 border border-gray-200 rounded-lg text-sm text-gray-800 font-medium select-text">
                                {{ child.attribute_value || '-' }}
                              </div>
                            <!-- 1. BOOLEAN -->
                            } @else if (isBooleanType(child.attribute_data_type)) {
                              <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  [checked]="child.attribute_value === 'true'"
                                  (change)="onBooleanChange(child, $event)"
                                  class="w-5 h-5 rounded cursor-pointer accent-blue-600 border-gray-300"
                                />
                                <span class="text-sm text-gray-600 font-medium">
                                  {{ (child.attribute_value === 'true' ? 'common.yes' : 'common.no') | translate }}
                                </span>
                              </label>

                            <!-- 2. FOREIGN KEY LOOKUP (e.g. app.school_locations.fk_location_id) -->
                            } @else if (isFkLookupType(child.attribute_data_type)) {
                              <select
                                [(ngModel)]="child.attribute_value"
                                (ngModelChange)="onSelectionChange(child, $event)"
                                class="flex-1 p-2 px-3 border border-gray-300 rounded-lg text-sm bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 shadow-sm"
                              >
                                <option [ngValue]="null">
                                  {{ 'portal.mydata.attributes.select' | translate }}
                                </option>
                                @for (opt of getFkOptions(child.attribute_data_type); track opt.value) {
                                  <option [value]="opt.value">{{ opt.label }}</option>
                                }
                              </select>

                            <!-- 3. PICKLIST / SELECTION_LIST -->
                            } @else if (isPicklistType(child.attribute_data_type)) {
                              <select
                                [(ngModel)]="child.attribute_value"
                                (ngModelChange)="onSelectionChange(child, $event)"
                                class="flex-1 p-2 px-3 border border-gray-300 rounded-lg text-sm bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 shadow-sm"
                              >
                                <option [ngValue]="null">
                                  {{ 'portal.mydata.attributes.select' | translate }}
                                </option>
                                @for (option of parseValueList(child.attribute_value_list); track option) {
                                  <option [value]="option">{{ option | translate }}</option>
                                }
                              </select>

                            <!-- 4. DATE -->
                            } @else if (isDateType(child.attribute_data_type)) {
                              <input
                                type="date"
                                [(ngModel)]="child.attribute_value"
                                (change)="onDateChange(child, $event)"
                                class="flex-1 p-2 px-3 border border-gray-300 rounded-lg text-sm bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 shadow-sm"
                              />

                            <!-- 5. NUMBER / INTEGER -->
                            } @else if (isNumberType(child.attribute_data_type)) {
                              <input
                                type="number"
                                [(ngModel)]="child.attribute_value"
                                (blur)="onTextBlur(child)"
                                (keydown.enter)="onTextEnter(child)"
                                class="flex-1 p-2 px-3 border border-gray-300 rounded-lg text-sm bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 shadow-sm"
                                placeholder="0"
                              />

                            <!-- 6. STRING / TEXT / DEFAULT -->
                            } @else {
                              <input
                                type="text"
                                [(ngModel)]="child.attribute_value"
                                (blur)="onTextBlur(child)"
                                (keydown.enter)="onTextEnter(child)"
                                class="flex-1 p-2 px-3 border border-gray-300 rounded-lg text-sm bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 shadow-sm"
                                placeholder="Wert eingeben..."
                              />
                            }

                            @if (savingAttributeId() === child.attribute_id) {
                              <span class="text-base animate-pulse">💾</span>
                            }
                          </div>
                        </div>
                      }
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      }

      @if (!initialLoading() && !error() && userAttributes().length === 0) {
        <div class="text-center p-10 text-gray-400 italic">
          Keine Attribute vorhanden.
        </div>
      }
    </div>
  `,
  styles: [],
})
export class UserAttributesComponent implements OnInit {
  readonly contactId = input.required<number>();

  private readonly attributeService = inject(AttributeDefinitionService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly roleService = inject(UserRoleService);

  // Use service error, but track initial load separately to avoid hiding attrs during saves
  readonly error = this.attributeService.error;
  readonly initialLoading = signal(true); // Only true until first successful load

  // Cached foreign key lookup options by data type key
  readonly fkOptionsMap = signal<Map<string, FkLookupOption[]>>(new Map());

  // Raw attributes from backend (before filtering)
  private readonly rawUserAttributes = this.attributeService.userAttributes;

  // Filtered attributes based on user role
  readonly userAttributes = computed(() => {
    const attributes = this.rawUserAttributes();
    if (!attributes || attributes.length === 0) {
      return [];
    }

    const isStudent = this.roleService.isStudent();
    const isLecturer = this.roleService.isLecturer();

    // If roles haven't finished loading yet (or none detected), show attributes where student_portal or teacher_portal is true
    if (!isStudent && !isLecturer) {
      return this.filterAttributesByRole(attributes, true, true);
    }

    // If user has both roles, show all attributes that are in EITHER portal
    if (isStudent && isLecturer) {
      return this.filterAttributesByRole(attributes, true, true);
    }

    // Filter attributes based on role
    return this.filterAttributesByRole(attributes, isStudent, isLecturer);
  });

  /**
   * Signal to indicate if there are any attributes to show
   */
  readonly hasVisibleAttributes = computed(() => this.userAttributes().length > 0);

  readonly savingAttributeId = signal<number | null>(null);
  readonly collapsedGroups = signal<Set<number>>(new Set<number>());

  private previousValues = new Map<number, string>();
  private enterPressed = false;

  constructor() {
    // Load attributes when contactId changes
    effect(() => {
      const id = this.contactId();
      if (id) {
        this.loadUserAttributes();
      }
    });
  }

  ngOnInit(): void {
    // Effect handles loading
  }

  loadUserAttributes(): void {
    this.attributeService.getUserAttributes(this.contactId()).subscribe({
      next: (attributes) => {
        // Mark initial load as done
        this.initialLoading.set(false);
        // Store initial values to detect changes
        attributes.forEach((group) => {
          if (group.children) {
            group.children.forEach((child) => {
              this.setPreviousValue(child.attribute_id, child.attribute_value || '');
            });
          }
        });
        // Pre-load FK options for all FK-type children (outside render cycle)
        queueMicrotask(() => this.preloadFkOptions(attributes));
      },
      error: (error: unknown) => {
        this.initialLoading.set(false);
        console.error('❌ Failed to load user attributes:', error);
      },
    });
  }

  /**
   * Pre-load FK lookup options for all FK-type attributes.
   * Called via queueMicrotask to ensure we are outside the Angular render cycle.
   */
  private preloadFkOptions(attributes: UserAttributeWithDefinition[]): void {
    const fkKeys = new Set<string>();
    attributes.forEach((group) => {
      (group.children || []).forEach((child) => {
        if (child.attribute_data_type && this.isFkLookupType(child.attribute_data_type)) {
          fkKeys.add(child.attribute_data_type.trim());
        }
      });
    });
    fkKeys.forEach((key) => this.loadFkOptions(key));
  }

  parseValueList(valueList: string | null | undefined): string[] {
    if (!valueList) return [];
    try {
      // Remove outer parentheses/brackets and split by comma
      const cleaned = valueList.replace(/^[\(\[\{]|[\)\]\}]$/g, '').trim();
      return cleaned
        .split(',')
        .map((v) => v.replace(/^["'\s]+|["'\s]+$/g, '').trim())
        .filter((v) => v.length > 0);
    } catch (error) {
      console.error('❌ Failed to parse value list:', valueList, error);
      return [];
    }
  }

  isBooleanType(dataType: string | null | undefined): boolean {
    if (!dataType) return false;
    const dt = dataType.trim().toLowerCase();
    return dt === 'boolean' || dt === 'bool';
  }

  isDateType(dataType: string | null | undefined): boolean {
    if (!dataType) return false;
    const dt = dataType.trim().toLowerCase();
    return dt === 'date' || dt === 'datetime' || dt === 'timestamp';
  }

  isNumberType(dataType: string | null | undefined): boolean {
    if (!dataType) return false;
    const dt = dataType.trim().toLowerCase();
    return dt === 'number' || dt === 'integer' || dt === 'int' || dt === 'float' || dt === 'decimal';
  }

  isPicklistType(dataType: string | null | undefined): boolean {
    if (!dataType) return false;
    const dt = dataType.trim().toLowerCase();
    return dt === 'picklist' || dt === 'selection_list' || dt === 'select' || dt === 'dropdown';
  }

  isFkLookupType(dataType: string | null | undefined): boolean {
    if (!dataType) return false;
    const dt = dataType.trim().toLowerCase();
    // Check if format contains schema.table or app.school_locations
    return dt.includes('app.school_locations') || dt.includes('school_locations') || /^[a-z_]+\.[a-z_]+/.test(dt);
  }

  /**
   * Return cached FK lookup options for a given data type key.
   * Returns [] while loading (pre-loading is triggered from loadUserAttributes, not here).
   */
  getFkOptions(dataType: string | null | undefined): FkLookupOption[] {
    if (!dataType) return [];
    const key = dataType.trim();
    return this.fkOptionsMap().get(key) ?? [];
  }

  private loadFkOptions(dataTypeKey: string): void {
    const currentMap = new Map(this.fkOptionsMap());
    if (currentMap.has(dataTypeKey)) return;

    // Set placeholder to prevent repeated calls
    currentMap.set(dataTypeKey, []);
    this.fkOptionsMap.set(currentMap);

    const lower = dataTypeKey.toLowerCase();

    // 1. School Locations: app.school_locations.fk_location_id(location_shortname - town)
    if (lower.includes('school_locations')) {
      // Extract label format in parenthesis if present, e.g. "location_shortname - town"
      const formatMatch = dataTypeKey.match(/\(([^)]+)\)/);
      const labelFormat = formatMatch ? formatMatch[1].trim() : 'location_shortname - town';

      // Step 1: Fetch the student's degrees to get their enrolled school IDs
      const degreesUrl = `${APP_SCHEMA_ENDPOINTS.contacts.list}${this.contactId()}/complete`;
      this.http.get<any>(degreesUrl).pipe(
        map((data) => {
          const degrees: any[] = data?.degrees || [];
          const schoolIds = [...new Set<number>(
            degrees
              .map((d: any) => d.school_id ?? d.fk_school_id)
              .filter((id: any): id is number => typeof id === 'number' && id > 0),
          )];
          console.log('🏫 Enrolled school IDs:', schoolIds);
          return schoolIds;
        }),
        catchError((err) => {
          console.error('❌ Failed to load contact degrees for FK filter:', err);
          return of([] as number[]);
        }),
      ).subscribe((schoolIds) => {
        if (schoolIds.length === 0) {
          this.loadAllSchoolLocations(dataTypeKey, labelFormat);
          return;
        }

        // Step 2: Fetch locations filtered by school
        this.http.get<any[]>(APP_SCHEMA_ENDPOINTS.schoolLocations.list).pipe(
          map((locations) => {
            // Filter to enrolled schools only
            return locations.filter(
              (loc) => loc.fk_school_id != null && schoolIds.includes(loc.fk_school_id),
            );
          }),
          catchError((err) => {
            console.error('❌ Failed to load school locations for FK lookup:', err);
            return of([] as any[]);
          }),
        ).subscribe((filteredLocations) => {
          console.log('📍 Filtered locations:', filteredLocations.length);
          const options: FkLookupOption[] = filteredLocations.map((loc: any) => {
            const parts = labelFormat.split('-').map((p: string) => p.trim());
            const renderedParts = parts
              .map((p: string) => loc[p] || '')
              .filter((v: string) => v.length > 0);
            const label = renderedParts.length > 0
              ? renderedParts.join(' - ')
              : `${loc.location_shortname} - ${loc.town || loc.location_name || ''}`;
            return { value: loc.location_id.toString(), label };
          });
          options.sort((a, b) => a.label.localeCompare(b.label));
          const updatedMap = new Map(this.fkOptionsMap());
          updatedMap.set(dataTypeKey, options);
          this.fkOptionsMap.set(updatedMap);
        });
      });
    }
  }

  private loadAllSchoolLocations(
    dataTypeKey: string,
    labelFormat: string,
  ): void {
    this.http.get<any[]>(APP_SCHEMA_ENDPOINTS.schoolLocations.list).pipe(
      catchError((err) => {
        console.error('❌ Failed to load school locations fallback:', err);
        return of([] as any[]);
      }),
    ).subscribe((locations) => {
      const options: FkLookupOption[] = locations.map((loc: any) => {
        const parts = labelFormat.split('-').map((p: string) => p.trim());
        const renderedParts = parts
          .map((p: string) => loc[p] || '')
          .filter((v: string) => v.length > 0);
        const label = renderedParts.length > 0
          ? renderedParts.join(' - ')
          : `${loc.location_shortname} - ${loc.town || loc.location_name || ''}`;
        return { value: loc.location_id.toString(), label };
      });
      options.sort((a, b) => a.label.localeCompare(b.label));
      const updatedMap = new Map(this.fkOptionsMap());
      updatedMap.set(dataTypeKey, options);
      this.fkOptionsMap.set(updatedMap);
    });
  }

  onDateChange(attribute: UserAttributeWithDefinition, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    if (value !== (attribute.attribute_value || '')) {
      attribute.attribute_value = value;
      this.setPreviousValue(attribute.attribute_id, value);
      this.updateAttribute(attribute, value);
    }
  }

  /**
   * Filter attributes based on user role and portal flags
   *
   * Logic:
   * - If student_portal is true, show to students
   * - If teacher_portal is true, show to lecturers
   * - If both roles, show all attributes
   * - Groups are shown if they have at least one visible child
   */
  private filterAttributesByRole(
    attributes: UserAttributeWithDefinition[],
    isStudent: boolean,
    isLecturer: boolean,
  ): UserAttributeWithDefinition[] {
    return attributes
      .map((group) => {
        // Filter children based on portal flags
        const filteredChildren = (group.children || []).filter((child) => {
          // If student, show attributes with student_portal = true
          if (isStudent && child.student_portal === true) {
            return true;
          }

          // If lecturer, show attributes with teacher_portal = true
          if (isLecturer && child.teacher_portal === true) {
            return true;
          }

          return false;
        });

        // Return group with filtered children
        return {
          ...group,
          children: filteredChildren,
        };
      })
      .filter((group) => {
        // Only show groups that have at least one visible child after filtering
        return group.children && group.children.length > 0;
      });
  }

  /**
   * Determine if an attribute is read-only for the user.
   * Teacher attributes (e.g. under 'teacher_attributes' group or with teacher_portal) are managed via SWS / HR and should not be edited here.
   */
  isReadOnlyAttribute(group: UserAttributeWithDefinition, child: UserAttributeWithDefinition): boolean {
    const groupName = (group.attribute_name || '').toLowerCase();
    const childName = (child.attribute_name || '').toLowerCase();
    return (
      groupName === 'teacher_attributes' ||
      childName === 'entry_date' ||
      childName === 'departure_date'
    );
  }

  /**
   * Unique track identifier for children, combining attribute_id with user_attribute_id / school_id
   */
  getChildTrackId(child: UserAttributeWithDefinition): string {
    return `${child.attribute_id}_${child.user_attribute_id ?? ''}_${child.fk_school_id ?? ''}`;
  }

  /**
   * Check if a group has BULLET_LIST items
   * Used to determine if radio button display should be used
   */
  hasBulletListItems(children: UserAttributeWithDefinition[] | undefined): boolean {
    if (!children) return false;
    return children.some((child) => child.attribute_data_type === 'BULLET_LIST');
  }

  /**
   * Handle BULLET_LIST radio button change
   * Sets selected item to true and all others in the group to false
   */
  onBulletListChange(
    group: UserAttributeWithDefinition,
    selectedChild: UserAttributeWithDefinition,
  ): void {
    if (!group.children) return;

    const contactId = this.contactId();

    // Update all BULLET_LIST items in this group
    group.children.forEach((child) => {
      if (child.attribute_data_type === 'BULLET_LIST') {
        const newValue = child.attribute_id === selectedChild.attribute_id ? 'true' : 'false';

        // Update local value immediately for UI feedback
        child.attribute_value = newValue;
        this.setPreviousValue(child.attribute_id, newValue);

        // Save to backend
        this.savingAttributeId.set(child.attribute_id);
        this.attributeService
          .updateUserAttribute(contactId, child.attribute_id, newValue)
          .subscribe({
            next: () => {
              this.savingAttributeId.set(null);
            },
            error: (error: unknown) => {
              this.savingAttributeId.set(null);
              console.error('❌ Failed to update bullet list attribute:', error);
              // Reload on error to revert to previous values
              this.loadUserAttributes();
            },
          });
      }
    });
  }

  onBooleanChange(attribute: UserAttributeWithDefinition, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.checked ? 'true' : 'false';
    // Update local value immediately for UI feedback
    attribute.attribute_value = value;
    this.setPreviousValue(attribute.attribute_id, value);
    this.updateAttribute(attribute, value);
  }

  onSelectionChange(attribute: UserAttributeWithDefinition, value: string | null): void {
    const stringVal = value !== null && value !== undefined ? value.toString().trim() : '';
    const previousVal = this.getPreviousValue(attribute.attribute_id);
    if (stringVal !== previousVal) {
      attribute.attribute_value = stringVal;
      this.setPreviousValue(attribute.attribute_id, stringVal);
      this.updateAttribute(attribute, stringVal);
      // Force re-render to sync display after selection
      this.cdr.markForCheck();
    }
  }

  onTextChange(attribute: UserAttributeWithDefinition, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();
    if (value !== (attribute.attribute_value || '')) {
      this.updateAttribute(attribute, value);
    }
  }

  onTextModelChange(attribute: UserAttributeWithDefinition, value: string | null): void {
    const trimmedValue = (value || '').trim();
    const currentValue = (attribute.attribute_value || '').trim();

    // Only update if value actually changed
    if (trimmedValue !== currentValue) {
      this.updateAttribute(attribute, trimmedValue);
    }
  }

  onTextBlur(attribute: UserAttributeWithDefinition): void {
    // Skip if Enter was just pressed (prevents double save)
    if (this.enterPressed) {
      this.enterPressed = false;
      return;
    }

    const currentValue = (attribute.attribute_value || '').trim();
    const previousValue = this.getPreviousValue(attribute.attribute_id);

    // Only save if value actually changed
    if (currentValue !== previousValue) {
      this.updateAttribute(attribute, currentValue);
    }
  }

  onTextEnter(attribute: UserAttributeWithDefinition): void {
    // Set flag to prevent blur from triggering save
    this.enterPressed = true;

    const currentValue = (attribute.attribute_value || '').trim();
    const previousValue = this.getPreviousValue(attribute.attribute_id);

    // Only save if value actually changed
    if (currentValue !== previousValue) {
      this.updateAttribute(attribute, currentValue);
    }

    // Reset flag after a short delay
    setTimeout(() => {
      this.enterPressed = false;
    }, 100);
  }

  private getPreviousValue(attributeId: number): string {
    return this.previousValues.get(attributeId) || '';
  }

  private setPreviousValue(attributeId: number, value: string): void {
    this.previousValues.set(attributeId, value);
  }

  toggleGroup(groupId: number): void {
    const collapsed = new Set(this.collapsedGroups());
    if (collapsed.has(groupId)) {
      collapsed.delete(groupId);
    } else {
      collapsed.add(groupId);
    }
    this.collapsedGroups.set(collapsed);
  }

  isGroupCollapsed(groupId: number): boolean {
    return this.collapsedGroups().has(groupId);
  }

  private updateAttribute(attribute: UserAttributeWithDefinition, value: string): void {
    this.savingAttributeId.set(attribute.attribute_id);

    this.attributeService
      .updateUserAttribute(this.contactId(), attribute.attribute_id, value)
      .subscribe({
        next: () => {
          this.savingAttributeId.set(null);
          this.setPreviousValue(attribute.attribute_id, value);
        },
        error: (error: unknown) => {
          this.savingAttributeId.set(null);
          console.error('❌ Failed to update attribute:', error);
          // Reload on error to revert to previous value
          this.loadUserAttributes();
        },
      });
  }

}
